import * as React from 'react';
import {Display} from "../../../components/Display";
import {Loader} from "../../../components/Utils/loader/Loader";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {CensusStatusPieChart} from "../../../processes/massive_actions/CensusCooler/widgets/CensusPieChart";
import {EmptyState, infoIcons} from "@vebox/etui-core";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {useLocation, useNavigate} from "react-router-dom";
import {
    createData,
    createFailPieCharData,
    createOkPieCharData,
    createPieCharData,
    getPieChartLegend
} from "../../../processes/massive_actions/CensusCooler/pages/CensusSummaryPage/utils";
import {isUUID} from "../../utils";
import './styles.scss'
import {CensusDetailsContext} from "../../providers/CensusDetailsProvider";

declare var AppTranslations: AppTranslations;
const DATA_PER_CHART = 6;

interface CensusChartsProps {
    isLoadingData: boolean;
    error: Error;
}

export const CensusCharts: React.FC<CensusChartsProps> = ({isLoadingData, error}) => {

    const {suggestedActions, censusEnums} = React.useContext(CensusDetailsContext)

    const location = useLocation();
    const navigate = useNavigate();

    const [isEmpty, setIsEmpty] = React.useState(false);


    const chartsData = React.useMemo(() => {
        if (!suggestedActions?.isLoading && suggestedActions?.data && !censusEnums?.isLoading) {
            return {
                generalData: createPieCharData(suggestedActions?.data, censusEnums?.data?.censusEquipmentStatuses),
                failData: createFailPieCharData(suggestedActions?.data, DATA_PER_CHART, censusEnums?.data),
                okData: createOkPieCharData(suggestedActions?.data, DATA_PER_CHART, censusEnums?.data)
            }
        }
        return {
            generalData: [],
            failData: {data: [], otherData: []},
            okData: {data: [], otherData: []}
        };
    }, [suggestedActions?.data, suggestedActions?.isLoading, censusEnums?.isLoading]);

    const legendItems = React.useMemo(() => {
        if (
            suggestedActions?.data?.summary &&
            suggestedActions?.data?.summary.length > 0
        ) {
            setIsEmpty(false);
            return getPieChartLegend({
                items: chartsData.generalData,
                total: chartsData.generalData.reduce((acc, item) => acc + item.frequency, 0),
            })
        } else {
            setIsEmpty(true);
            return []
        }
    }, [chartsData.generalData]);

    const legendFailItems = React.useMemo(() => {
        if (
            suggestedActions?.data?.summary &&
            suggestedActions?.data?.summary.length > 0
        ) {
            setIsEmpty(false);
            return {...createData({data: chartsData.failData.data, otherData: chartsData.failData.otherData})}
        } else {
            setIsEmpty(true);
            return {data: [], otherData: []}
        }
    }, [chartsData.failData]);

    const legendOkItems = React.useMemo(() => {
        if (
            suggestedActions?.data?.summary &&
            suggestedActions?.data?.summary.length > 0
        ) {
            setIsEmpty(false);
            return {...createData({data: chartsData.okData.data, otherData: chartsData.okData.otherData})}
        } else {
            setIsEmpty(true);
            return {data: [], otherData: []}
        }
    }, [chartsData.okData]);


    React.useEffect(() => {
        if (location && !isUUID(location?.pathname.replace("/", ""))) {
            navigate("/");
        }
    }, []);

    return (
        <div className={"census-info-details"}>
            <Display isVisible={(isLoadingData || suggestedActions?.isLoading) && !error}>
                <div className={"loader-container-100"}>
                    <Loader relativelyCentered={true}/>
                </div>
            </Display>
            <Display isVisible={!isEmpty && !(isLoadingData || suggestedActions?.isLoading)}>
                <div className={"census-detail-card-container"}>
                    <div className="widget-content">
                        <VeBoxCard className={"left-widget-content"}>
                            <CensusStatusPieChart
                                data={chartsData.generalData}
                                legendData={legendItems}
                                cartLegendClassName={"chart-legend"}
                                chartClassName={"general-chart"}
                                noDataMessage={"0"}
                                title={AppTranslations.massiveActions.censusCooler.censusResultTitle}
                            />
                        </VeBoxCard>
                        <VeBoxCard className={"top-widget-content"}>
                            <CensusStatusPieChart
                                data={chartsData.failData.data}
                                legendData={legendFailItems?.data}
                                otherLegendData={legendFailItems?.otherData}
                                cartLegendClassName={"fail-chart-legend"}
                                chartClassName={"fail-chart"}
                                noDataMessage={"0"}
                                title={AppTranslations.massiveActions.censusCooler.censusResultTitleFailDistribution}
                            />
                        </VeBoxCard>
                        <VeBoxCard className={"bottom-widget-content"}>
                            <CensusStatusPieChart
                                data={chartsData.okData.data}
                                legendData={legendOkItems?.data}
                                cartLegendClassName={"ok-chart-legend"}
                                chartClassName={"ok-chart"}
                                otherLegendData={legendOkItems?.otherData}
                                noDataMessage={"0"}
                                title={AppTranslations.massiveActions.censusCooler.censusResultTitleOkDistribution}
                            />
                        </VeBoxCard>
                    </div>
                </div>
            </Display>
            <Display isVisible={isEmpty && !(isLoadingData || suggestedActions?.isLoading)}>
                <div className={"empty-container"}>
                    <EmptyState>
                        <EmptyState.InfoIcon
                            styles={{
                                root: {
                                    color: "var(--empty-indicator-svg-stroke)",
                                },
                            }}
                            glyph={infoIcons.NoContent}
                        />
                        <EmptyState.Title color="var(--empty-indicator-title-color)">
                            {AppTranslations.noDataAvailable}
                        </EmptyState.Title>
                        <EmptyState.SubTitle color="var(--empty-indicator-subtitle-color)">
                            {AppTranslations.massiveActions.censusCooler.noCensusDetails}
                        </EmptyState.SubTitle>
                    </EmptyState>
                </div>
            </Display>
        </div>
    );
}